import dynamic from 'next/dynamic';
import { getReadableImageUrl } from "@/lib/directus";

const AnimatedSection = dynamic(() => import('../../components/Animations/useAnimations'), { ssr: false });
const SuccessStoriesClient = dynamic(() => import('./SuccessStoriesClient'), { ssr: false });
const SuccessStoriesClientFallback = dynamic(() => import('./SuccessStoriesClientFallback'), { ssr: false });
const ScreenAwareContent = dynamic(() => import('../../utils/screenAwareContent'), { 
  ssr: false,
  loading: () => <div className="min-h-screen h-screen" /> // Simple loading state
});

interface SuccessStoriesData {
  heading?: {
    text?: string;
    title?: string;
  };
  button?: {
    label?: string;
    href?: string;
  };
  linked_images?: {
    site_content_collections_id: {
      collection: Array<{
        collection: string;
        item: {
          // For images collection
          alt?: string;
          image_file?: {
            image: {
              id: string;
              filename_download: string;
            };
          };
          // For menu_items collection
          href?: string;
          label?: string;
        };
      }>;
    };
  }[];
}

export const fields = [
  // heading
  "heading.text",
  "heading.title",
  
  // button
  "button.label",
  "button.href",
  
  // linked_images collection fields

  "linked_images.id",
  
  "linked_images.site_content_collections_id.collection.item:images.alt",
  "linked_images.site_content_collections_id.collection.item:images.image_file.image.id",
  "linked_images.site_content_collections_id.collection.item:images.image_file.image.filename_download",

  "linked_images.site_content_collections_id.collection.item:menu_items.href",
  "linked_images.site_content_collections_id.collection.item:menu_items.label",
] as const;

interface SuccessStoriesProps {
  data?: SuccessStoriesData;
  zIndex: number;
  thresholds: number[][];
  transitionTime?: number;
  mobileThresholds: number[][];
  helperRotation?: number;
}

export default function SuccessStories({ data, zIndex, thresholds, transitionTime, mobileThresholds, helperRotation }: SuccessStoriesProps) { 
  // Extract image and link information from linked_images
  const imageData = data?.linked_images?.map(collection => {
    // Extract image data from the collection
    const imageItem = collection?.site_content_collections_id?.collection.find(
      item => item.item && item.item.image_file
    )?.item;
    
    // Extract menu item data from the collection
    const menuItem = collection?.site_content_collections_id?.collection.find(
      item => item.item && item.item.href
    )?.item;
    
    // Extract image data
    const imageId = imageItem?.image_file?.image?.id;
    const imageFilename = imageItem?.image_file?.image?.filename_download;
    const alt = imageItem?.alt || '';
    
    // Extract link data
    const link = menuItem?.href || '';
    const linkLabel = menuItem?.label || '';
    
    const imageUrl = imageId && imageFilename ? getReadableImageUrl(imageId, imageFilename) : '';
    
    return {
      imageUrl,
      alt,
      link,
      linkLabel
    };
  }) || [];


  // Extract just the image URLs for the client components
  const imageUrls = imageData.map(item => item.imageUrl).filter(Boolean);

  return (
		<ScreenAwareContent
			supportedContent={
        <AnimatedSection
          className="h-screen overflow-hidden fixed w-full pt-16 xl:pt-32  top-0 left-0"
          thresholds={[thresholds?.[0]?.[0], thresholds?.[thresholds.length - 1]?.[1]]}
          mobileThresholds={[mobileThresholds?.[0]?.[0], mobileThresholds?.[mobileThresholds.length - 1]?.[1]]}
          zIndex={zIndex}
          withZIndex={true}
          transitionTime={transitionTime}
          helperRotation={helperRotation}
          variants={{
            desktop: {
              visible: { y: 0, opacity: 1 },
              above: { y: "100vh", opacity: 1 },
              below: { y: "-100vh", opacity: 1 },
            },
            mobile: {
              visible: { y: 0, opacity: 1 },
              above: {  y: 0, opacity: 0 },
              below: { y: 0, opacity: 0 },
            }
          }}
        >
          <div className="gl-container relative h-full flex items-start lg:items-center">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between lg:gap-24 w-full ">
              {/* Text Content */}
              <div className="f-full lg:w-[40%]">
                <AnimatedSection 
                  section="div" 
                  className="gl-desktop-scale"
                  mobileThresholds={[mobileThresholds?.[0]?.[0], mobileThresholds?.[mobileThresholds.length - 1]?.[1]]}
                  variants={{
                    desktop: {
                      visible: { opacity: 1, x: 0 },
                      above: {  opacity: 1, x: 0 },
                      below: { opacity: 1, x: 0 },
                    },
                    mobile: {
                      visible: { opacity: 1, x: 0 },
                      above: {  opacity: 1, x: "-200vw" },
                      below: { opacity: 1, x: 0 },
                    }
                  }}
                >
                  <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-primary tracking-wide mb-4">
                    {data?.heading?.title}
                  </h2>

                  {/* Button moved to bottom for mobile */}
                  <a 
                    href={data?.button?.href || '/results'} 
                    className="hidden lg:inline-block px-12 py-2.5 bg-primary rounded-3xl text-white text-xl font-greycliff leading-7 tracking-tight hover:bg-secondary transition-colors"
                  >
                    {data?.button?.label || 'See Our Results'}
                  </a>
                </AnimatedSection>
              </div>

              {/* Images Section */}
              <SuccessStoriesClient 
                images={imageData} 
                mobileThresholds={[mobileThresholds?.[0]?.[0], mobileThresholds?.[mobileThresholds.length - 1]?.[1]]} 
                buttonLabel={data?.button?.label}
              />
            </div>
          </div>
        </AnimatedSection>
			}
			fallbackContent={
        <section className="overflow-hidden w-full lg:py-16 bg-lightGray">
          <div className="gl-container relative h-full flex items-start lg:items-center">
            <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between xl:gap-24 w-full ">
              {/* Text Content */}
              <div className="f-full xl:w-[40%]">
                <div className="gl-desktop-scale">
                  <h2 className="text-[2rem] font-essonnes font-light leading-tight md:text-5xl lg:text-[3.375rem] text-primary tracking-wide mb-4">
                    {data?.heading?.title}
                  </h2>

                  {/* Button moved to bottom for mobile */}
                  <a 
                    href={data?.button?.href || '/results'} 
                    className="hidden xl:inline-block px-12 py-2.5 bg-primary rounded-3xl text-white text-xl font-greycliff leading-7 tracking-tight hover:bg-secondary transition-colors"
                  >
                    {data?.button?.label || 'See Our Results'}
                  </a>
                </div>
              </div>

              {/* Images Section */}
              <SuccessStoriesClientFallback 
                images={imageData}
                buttonLabel={data?.button?.label}
              />
            </div>
          </div>
        </section>
			}
    />
  );
}
